import { gql } from "lib/apolloClient";
import {
  attachmentFragment,
  similarityFragment,
  pypElementRatingFragment,
  projectGroupFragments,
  studentsAlertFragment,
  userNameFragment,
  staffFragment,
  studentFragment,
} from "modules/CommonFragments";
import { projectAttachmentFragment } from "UnitPlans/modules/UnitPlanFragments";
import { messageCommentOfPostFragment } from "Post/modules/PostFragments";

/*******************************************************************************************************
 * This is a basic user fragment dont add anything specific like courses, subjects etc
 * Please create a new one for that
 ********************************************************************************************************/

export const projectUserFragment = {
  basicUser: gql`
    fragment userItem on User {
      ...userNameFields
      email
      profileImage
      ... on Student {
        sourceId
        registrationCategory
      }
      ... on Staff {
        role
        isArchived
      }
    }
    ${userNameFragment}
  `,
  basicUserWithIsArchivedV2: gql`
    fragment userItem on User {
      ...userNameFields
      email
      profileImage
      ... on Student {
        sourceId
        alertTypes {
          ...alertTypeFields
        }
      }
      ... on Staff {
        role
        isArchived
        isArchivedV2(filters: { academicYearId: $academicYearId })
      }
    }
    ${studentsAlertFragment}
    ${userNameFragment}
  `,
};

export const supervisorFragments = {
  supervisorInfoFragment: gql`
    fragment supervisorInfo on Staff {
      ...userNameFields
      email
      profileImage
      curriculumPrograms {
        acronym
        id
      }
      isArchived
    }
    ${userNameFragment}
  `,
  supervisorInfoFragmentWithIsArchivedV2: gql`
    fragment supervisorInfo on Staff {
      ...userNameFields
      email
      profileImage
      curriculumPrograms {
        acronym
        id
      }
      isArchived
      isArchivedV2(filters: { academicYearId: $academicYearId })
      role
    }
    ${userNameFragment}
  `,
  supervisorInfoFragmentV2: gql`
    fragment SupervisorInfoItemV2 on Staff {
      ...userNameFields
      email
      profileImage
      role
      curriculumPrograms {
        id
        acronym
      }
      isArchived
      isArchivedV2(filters: { academicYearId: $academicYearId })
      otherDetails {
        id
        organizationName
        phoneNumber
      }
    }
    ${userNameFragment}
  `,
};

export const projectGroupSupervisor = {
  projectGroupSupervisorApprovalInfo: gql`
    fragment projectGroupSupervisorApprovalInfoItem on ProjectGroupSupervisor {
      id
      staff {
        ...supervisorInfo
        role
        otherDetails {
          id
          organizationName
          phoneNumber
        }
        isArchived
        isArchivedV2(filters: { academicYearId: $academicYearId })
        curriculumPrograms {
          id
          acronym
        }
      }
      approvalStatus
      invitedBy {
        ...userNameFields
        profileImage
        ... on Student {
          alertTypes {
            ...alertTypeFields
          }
        }
      }
    }
    ${studentsAlertFragment}
    ${supervisorFragments.supervisorInfoFragment}
    ${userNameFragment}
  `,
};

export const studentFragments = {
  studentInfoFragment: gql`
    fragment studentInfo on Student {
      ...userNameFields
      profileImage
      sourceId
      alertTypes {
        ...alertTypeFields
      }
    }
    ${studentsAlertFragment}
    ${userNameFragment}
  `,
  studentInfoFragmentV2: gql`
    fragment studentInfo on Student {
      ...userNameFields
      profileImage
      sourceId
      alertTypes {
        ...alertTypeFields
      }
      courseMap(filters: $studentCourseFilters) @skip(if: $skipCourseDetails) {
        id
        tags {
          id
          label
          type
        }
        subjectWiseLevel {
          level {
            id
            type
            label
          }
        }
      }
    }
    ${studentsAlertFragment}
    ${userNameFragment}
  `,
};

export const deadlineFragments = {
  deadlineFragment: gql`
    fragment deadline on ProjectGroupDeadline {
      id
      deadlineDate
      title
    }
  `,
  calendarDeadlineFragment: gql`
    fragment deadline on ProjectGroupDeadline {
      id
      deadlineDate
      deadlineTitle: title
      description
      isDeadlineAllDay
      statusWiseProjects(filters: $statusWiseProjectFilters) {
        completedProjects {
          id
          students {
            ...userNameFields
            profileImage
          }
        }
        pendingProjects {
          id
          students {
            ...userNameFields
            profileImage
          }
        }
      }
      projectGroup {
        ...projectGroupInfo
        yearGroup {
          id
          name
        }
      }
      creatorUser: createdBy {
        ...supervisorInfo
      }
    }
    ${projectGroupFragments.projectGroupInfo}
    ${supervisorFragments.supervisorInfoFragment}
    ${userNameFragment}
  `,
  calendarProjectDeadlineFragment: gql`
    fragment projectDeadline on ProjectDeadline {
      id
      project {
        id
      }
      deadline {
        id
        deadlineTitle: title
        deadlineDate
        description
        isDeadlineAllDay
        projectGroup {
          ...projectGroupInfo
          yearGroup {
            id
            name
          }
        }
        creatorUser: createdBy {
          ...supervisorInfo
        }
      }
      isDeadlineCompleted
    }
    ${projectGroupFragments.projectGroupInfo}
    ${supervisorFragments.supervisorInfoFragment}
  `,
  deadlineFragmentWithStatusCount: gql`
    fragment projectDeadline on ProjectGroupDeadline {
      id
      deadlineDate
      title
      description
      isDeadlineAllDay
      statusWiseProjects(filters: $statusWiseProjectFilters)
        @include(if: $fetchStatusWiseProjects) {
        completedProjects {
          id
          students {
            ...studentInfo
          }
        }
        pendingProjects {
          id
          students {
            ...studentInfo
          }
        }
      }
    }
    ${studentFragments.studentInfoFragment}
  `,
  calendarDeadlineDetailsFragment: gql`
    fragment deadlineDetails on ProjectGroupDeadline {
      id
      deadlineDate
      deadlineTitle: title
      description
      isDeadlineAllDay
      statusWiseProjects(filters: $statusWiseProjectFilters) {
        completedProjects {
          id
          students {
            id
            type
            ...userNameFields @skip(if: $skipUserDetails)
            profileImage @skip(if: $skipUserDetails)
          }
        }
        pendingProjects {
          id
          students {
            id
            type
            ...userNameFields @skip(if: $skipUserDetails)
            profileImage @skip(if: $skipUserDetails)
          }
        }
      }
      projectGroup {
        ...projectGroupInfo
        yearGroup {
          id
          name
        }
      }
      creatorUser: createdBy {
        ...supervisorInfo
      }
    }
    ${projectGroupFragments.projectGroupInfo}
    ${supervisorFragments.supervisorInfoFragment}
    ${userNameFragment}
  `,
  calendarProjectGroupTaskFragment: gql`
    fragment projectGroupTaskItem on ProjectGroupTask {
      id
      isDeadlineAllDay
      deadlineTitle: title
      description
      dueOn
      closesOn
      isStudentSubmissionEnabled
      status

      responses {
        id
        isComplete
        isSubmissionTurnedIn
        latestSubmissionResponse {
          id
          status
        }

        project {
          id
          students {
            id
            type
            ...userNameFields @skip(if: $skipUserDetails)
            profileImage @skip(if: $skipUserDetails)
            alertTypes @skip(if: $skipUserDetails) {
              ...alertTypeFields
            }
          }
        }
      }

      resources {
        edges {
          node {
            id
            label
            attachment {
              id
              name
              type
              mimeType
              url
              signedUrl
              thumbUrl
              title
              metadata
              streamUrl
              parentType
            }
          }
        }
      }

      submission {
        id
        type
        items {
          id
          template {
            id
            name
            type
            mimeType
            url
            signedUrl
            thumbUrl
            title
            metadata
            streamUrl
            parentType
          }
        }
      }

      creatorUser: createdBy {
        ...supervisorInfo
      }

      projectGroup {
        ...projectGroupInfo
        yearGroup {
          id
          name
        }
      }
    }
    ${studentsAlertFragment}
    ${projectGroupFragments.projectGroupInfo}
    ${userNameFragment}
  `,
  calendarProjectTaskFragment: gql`
    fragment projectTaskItem on ProjectTaskResponse {
      id
      mappedProject: project {
        id
      }
      isDeadlineCompleted: isComplete
      task {
        id
        dueOn
        deadlineTitle: title
        isDeadlineAllDay
        projectGroup {
          ...projectGroupInfo
          yearGroup {
            id
            name
          }
        }
        creatorUser: createdBy {
          ...supervisorInfo
        }
      }
    }
    ${projectGroupFragments.projectGroupInfo}
    ${supervisorFragments.supervisorInfoFragment}
  `,
};

export const projectFieldsFragment = {
  projectFieldsDetails: gql`
    fragment projectFieldsDetails on Project {
      id
      fields(filters: $filters) {
        id
        uid
        value
        comments {
          unreadCountV2
        }
        resolvedMinimalTree {
          id
          type
          ... on ResolvedFieldPlannerElementSet {
            id
            type
            ... on ResolvedFieldPlannerElementSet {
              id
              type
              nodes {
                id
                label
                type
                metadata
              }
            }
          }
        }
      }
    }
  `,
};

export const projectFragments = {
  projectInfoProgressFragment: gql`
    fragment projectInfoProgress on Project {
      id
      status
      type
      reportStatus
      isCompleted
      isArchived
      archivedBy {
        id
      }
      isRead
      allPostsCount
      portfolio {
        id
        isComplete
      }
      posts @skip(if: $isServiceAsAction) {
        totalCount
      }
      createdBy {
        id
      }
      updatedAt
      updatedBy {
        ...userNameFields
      }
      cascadeUpdateAt
      cascadeUpdateBy {
        ...userNameFields
      }
      academicHonesty(filters: { orderBy: DISPLAY_SEQUENCE })
        @skip(if: $isServiceAsAction) {
        id
        description
        displaySequence
        lockingState {
          state
        }
        createdBy {
          ...userNameFields
        }
      }
      students {
        ...studentInfo
        registrationCategory
      }
      supervisors {
        ...supervisorInfo
      }
      academicHonestyRemarks @skip(if: $isServiceAsAction) {
        id
        lockingState {
          state
        }
      }
      completedDeadlines @skip(if: $isServiceAsAction) {
        deadline {
          id
        }
      }
      isAcademicHonestyLocked @skip(if: $isServiceAsAction)
      ibPYPElementRatings @skip(if: $isServiceAsAction) {
        id
        ibPYPElementId
        value
        academicCriteriaSet {
          criteriaType
          id
        }
        academicCriteriaValue {
          id
          abbreviation
          label
          color
        }
      }
    }
    ${supervisorFragments.supervisorInfoFragmentWithIsArchivedV2}
    ${studentFragments.studentInfoFragmentV2}
    ${similarityFragment.similarityItem}
    ${userNameFragment}
  `,
  portfolioProjectInfoFragment: gql`
    fragment portfolioProjectInfo on Project {
      id
      status
      type
      reportStatus
      allPostsCount
      isRead
      isCasExternalPortingDone
      isArchived
      archivedBy {
        id
      }
      students {
        ...userNameFields
        profileImage
      }
      supervisors {
        ...userNameFields
        profileImage
        isArchived
        isArchivedV2(filters: { academicYearId: $academicYearId })
      }
      cascadeUpdateAt
      cascadeUpdateBy {
        ...userNameFields
      }
      updatedBy {
        ...userNameFields
      }
    }
    ${userNameFragment}
  `,
  projectCompletedDeadlinesFragment: gql`
    fragment projectCompletedDeadlinesItem on Project {
      id
      reportStatus
      markedCompletedBy {
        ...userNameFields
      }
      markedCompletedAt
      updatedAt
      updatedBy {
        ...userNameFields
      }
      completedDeadlines {
        deadline {
          id
        }
      }
    }
    ${userNameFragment}
  `,
  projectInfoFragment: gql`
    fragment projectInfoItem on Project {
      id
      supervisors {
        ...supervisorInfo
      }
      students {
        ...studentInfo
      }
      status
      #we only need collaborator and we can get the owner from the students so no need for extra filter
      studentCollaborators(filters: { roles: [COLLABORATOR] }) {
        student {
          ...studentInfo
        }
      }
      reportStatus
      finalProjectReports {
        id
      }
      isAcademicHonestyLocked
      ibPYPElementRatings {
        ...basicIBPYPElementRatingItem
      }
    }
    ${pypElementRatingFragment.basicIBPYPElementRating}
    ${supervisorFragments.supervisorInfoFragment}

    ${studentFragments.studentInfoFragment}
  `,
  projectDetailsHeaderInfoFragment: gql`
    fragment projectInfoItem on Project {
      id
      supervisors {
        ...supervisorInfo
      }
      students {
        ...studentInfo
      }
      status
      #we only need collaborator and we can get the owner from the students so no need for extra filter
      studentCollaborators(filters: { roles: [COLLABORATOR] }) {
        student {
          ...studentInfo
        }
      }
      reportStatus
      finalProjectReports {
        id
      }
    }
    ${supervisorFragments.supervisorInfoFragmentWithIsArchivedV2}
    ${studentFragments.studentInfoFragment}
  `,
  projectStudentInfoFragment: gql`
    fragment projectStudentInfo on Project {
      id
      students @skip(if: $skipStudentDetails) {
        ...userNameFields
        profileImage
        sourceId
        alertTypes {
          ...alertTypeFields
        }
        courseMap(filters: $studentCourseFilters)
          @skip(if: $skipCourseDetails) {
          id
          tags {
            id
            label
            type
          }
          subjectWiseLevel {
            level {
              id
              type
              label
            }
          }
        }
      }
    }
    ${studentsAlertFragment}
    ${userNameFragment}
  `,
  deletedProjectInfoProgressFragment: gql`
    fragment projectInfoProgress on Project {
      id
      status
      type
      reportStatus
      createdBy {
        id
        __typename
      }
      deletedAt
      deletedBy {
        ...userNameFields
        ... on Student {
          alertTypes {
            ...alertTypeFields
          }
        }
      }
      students {
        ...studentInfo
      }
      supervisors {
        ...supervisorInfo
      }
    }
    ${studentsAlertFragment}
    ${supervisorFragments.supervisorInfoFragmentWithIsArchivedV2}
    ${studentFragments.studentInfoFragment}
    ${userNameFragment}
  `,
};

export const projectAcademicHonestyFragments = {
  interviewInfoFragment: gql`
    fragment interviewInfoItem on ProjectAcademicHonesty {
      id
      description
      meetingDate
      createdBy {
        ...userItem
      }
      isPrivate
      isFormalInterview
      attachments {
        ...attachmentItem
      }
    }
    ${attachmentFragment.attachment}
    ${projectUserFragment.basicUser}
  `,
  formalInterviewFragment: gql`
    fragment formalInterviewInfoItem on ProjectAcademicHonesty {
      id
      description
      meetingDate
      isLocked
      attachments {
        ...attachmentItem
      }
    }
    ${attachmentFragment.attachment}
  `,
  formalInterviewFragmentV2: gql`
    fragment formalInterviewInfoItemV2 on ProjectAcademicHonesty {
      id
      description
      meetingDate
      displaySequence
      isRead
      lockingState {
        state
        scheduledAt
      }
      createdBy {
        ...userItem
      }
      attachments(
        filters: { orderByDirection: ASC, orderBy: DISPLAY_SEQUENCE }
      ) {
        ...attachmentItem
        displaySequence
      }
    }
    ${attachmentFragment.attachment}
    ${projectUserFragment.basicUser}
  `,
};

export const feedbackPostFragment = {
  feedbackPost: gql`
    fragment feedbackPostItem on Post {
      id
      title
      isRead
      isHiddenFromStudents
      createdBy {
        ...userItem
      }
      updatedBy {
        ...userItem
      }
      updatedAt
      createdAt
      attachments {
        ...attachmentItem
        displaySequence
      }
      comments {
        totalCount
        unreadCount
        unreadCountV2
        edges: messageEdges(first: 1) {
          node {
            ...messageItem
          }
        }
        unreadCommentsEdges: unreadMessageEdges {
          node {
            ...messageItem
          }
        }
      }
    }
    ${projectUserFragment.basicUser}
    ${attachmentFragment.attachment}
    ${messageCommentOfPostFragment.message}
  `,
};

export const studentPortfolioProgressFragment = {
  studentPortfolioProgress: gql`
    fragment portfolioProgress on StudentProjectPortfolio {
      id
      isRead
      status
      feedback {
        totalCount
      }
      completionStatus(filters: $completionStatusFilters) {
        isComplete
        markedCompletedAt
        markedCompletedBy {
          ...userNameFields
        }
      }
      interviews(
        filters: {
          isFormalInterview: true
          orderBy: DISPLAY_SEQUENCE
          orderByDirection: ASC
        }
      ) {
        totalCount
        edges {
          node {
            id
            isLocked
            meetingDate
            attachments {
              id
            }
            description
            lockingState {
              state
            }
            displaySequence
          }
        }
      }
      achievedPlannerElements {
        totalCount
        edges {
          node {
            label
            id
            metadata
          }
        }
      }
      strandCountsV2
    }
    ${userNameFragment}
  `,
};

export const studentProjectPortfolioFragments = {
  studentProjectPortfolioInterviewsFragment: gql`
    fragment studentProjectPortfolioInterviewsItem on StudentProjectPortfolio {
      id
      interviews {
        edges {
          node {
            ...interviewInfoItem
          }
        }
      }
      interviewAndFeedbackCounts {
        interviewCount
      }
    }
    ${projectAcademicHonestyFragments.interviewInfoFragment}
  `,
  studentProjectPortfolioDetailsFragment: gql`
    fragment studentProjectPortfolioDetailsItem on StudentProjectPortfolio {
      ...portfolioProgress
      id
      student {
        ...userItem
      }
      projectCounts {
        projectsCount(filters: $projectCountFilter)
        experiencesCount
      }
      notificationBadgeCounts {
        interestsAndGoalsCommentCount
        learningOutcomesCommentCount
        feedbackCount
      }
      completionStatus(filters: $completionStatusFilters) {
        isComplete
        markedCompletedAt
        markedCompletedBy {
          ...userNameFields
        }
      }
    }
    ${projectUserFragment.basicUser}
    ${studentPortfolioProgressFragment.studentPortfolioProgress}
    ${userNameFragment}
  `,
  studentProjectPortfolioFeedbackFragment: gql`
    fragment studentProjectPortfolioFeedbackItem on StudentProjectPortfolio {
      id
      feedback {
        totalCount
        edges {
          node {
            ...feedbackPostItem
          }
        }
      }
    }
    ${feedbackPostFragment.feedbackPost}
  `,
};

export const guidingQuestionsFragment = {
  guidingQuestions: gql`
    fragment guidingQuestions on ProjectGroupGuidingQuestion {
      id
      question
      grades {
        id
        name
      }
    }
  `,
};

export const insightFragments = {
  plannerElementInsightSpecific: gql`
    fragment plannerElementInsightSpecificItem on ProjectPlannerElementInsightSpecific {
      student {
        ...studentInfo
      }
      studentInsights {
        node {
          id
        }
        addressedInProjects {
          id
        }
        evidenceInProjects {
          id
        }
        evidenceInPosts {
          id
        }
      }
    }
    ${studentFragments.studentInfoFragment}
  `,
};

export const yearGroupFragments = {
  basicYearGroupDetails: gql`
    fragment basicYearGroupDetails on YearGroup {
      id
      name
      grade(filters: $gradeFilters) {
        id
        name
      }
      isArchived
    }
  `,
};

export const projectGroupFragment = {
  projectGroupWithAssessmentTools: gql`
    fragment projectGroupWithAssessmentToolsItem on ProjectGroup {
      assessmentTools(filters: $assessmentToolFilters) {
        id
        ... on MypObjectiveRubric {
          id
          label
          genericTags {
            id
            label
          }
          criteria {
            id
            label
            type
            highestMark
            parent
            children
            subText
            objectiveRubricDescriptors {
              id
              achievementLevel {
                id
                label
                subText
              }
              label
            }
          }
        }
      }
    }
  `,
  basicOrganizationElementRatings: gql`
    fragment basicOrganizationElementRatingsItem on OrganizationElementRatings {
      id
      ibPYPParentElementId
      subType
      academicCriteriaSets {
        label
        id
        criteriaType
        academicCriteriaValues {
          label
          description
          id
          abbreviation
          displaySequence
        }
      }
    }
  `,
  projectGroupCourses: gql`
    fragment projectGroupCoursesItem on ProjectGroup {
      id
      projects(filters: $projectGroupProjectFilters) {
        totalCount
      }
      courses {
        edges {
          node {
            id
            title
            students(filters: $courseFilters) {
              edges {
                node {
                  id
                  projects(filters: $projectFilters) {
                    id
                    title {
                      id
                      value
                    }
                  }
                  ...studentInfo
                }
              }
            }
          }
        }
      }
    }
    ${studentFragments.studentInfoFragment}
  `,
  projectGroupBasicDetails: gql`
    fragment projectGroupBasicDetails on ProjectGroup {
      id
      name
      subType
      isEnabled
      type
      curriculumProgram {
        id
        type
      }
    }
  `,
  projectGroupSubjectGroupSubjectDetails: gql`
    fragment projectGroupSubjectGroupSubjectDetailsItem on ProjectGroup {
      id
      subjectGroup @skip(if: $isAccountDepartmentEnabled) {
        id
        subjects(
          filters: {
            includeInterdisciplinarySubjects: $includeInterdisciplinarySubjects
          }
        ) {
          id
          name
          students(filters: $studentFilters) {
            edges {
              node {
                ...studentInfo
                courses(filters: $courseFilters) {
                  id
                  title
                }
                projects(filters: $projectFilters) {
                  id
                  title {
                    id
                    value
                  }
                }
              }
            }
          }
        }
      }
      scienceSubjects(
        filters: {
          includeInterdisciplinarySubjects: $includeInterdisciplinarySubjects
        }
      ) @include(if: $isAccountDepartmentEnabled) {
        id
        name
        students(filters: $studentFilters) {
          edges {
            node {
              ...studentInfo
              courses(filters: $courseFilters) {
                id
                title
              }
              projects(filters: $projectFilters) {
                id
                title {
                  id
                  value
                }
              }
            }
          }
        }
      }
    }
    ${studentFragments.studentInfoFragment}
  `,
  projectGroupSubjectDetails: gql`
    fragment projectGroupSubjectDetailsItem on ProjectGroup {
      id
      subjectGroup @skip(if: $isAccountDepartmentEnabled) {
        id
        subjects(filters: $subjectFilters) {
          id
          name
          students(filters: $studentFilters) {
            edges {
              node {
                ...studentInfo
              }
            }
          }
        }
      }
      scienceSubjects(filters: $subjectFilters)
        @include(if: $isAccountDepartmentEnabled) {
        id
        name
        students(filters: $studentFilters) {
          edges {
            node {
              ...studentInfo
            }
          }
        }
      }
    }
    ${studentFragments.studentInfoFragment}
  `,
  projectGroupTotalProjectsCount: gql`
    fragment projectGroupTotalProjectsCountItem on ProjectGroup {
      id
      name
      projects {
        totalCount
      }
    }
  `,
  projectGroupInfoV2: gql`
    fragment projectGroupInfoV2 on ProjectGroup {
      id
      name
      type
      isEnabled
      category
      yearGroup {
        ...basicYearGroupDetails
      }
      projects(filters: $projectFilters) {
        totalCount
      }
      studentProjectPortfolio(filters: $studentProjectPortfolioFilters) {
        totalCount
      }
      subType
    }
    ${yearGroupFragments.basicYearGroupDetails}
  `,
  projectGroupInfoBasicDetails: gql`
    fragment projectGroupInfoBasicDetails on ProjectGroup {
      id
      name
      type
      yearGroup {
        id
        name
      }
      subType
      subject {
        id
        name
        ibOfferedSubject {
          id
          contentSubject {
            id
            assessmentYearTitle
          }
        }
      }
      courses {
        edges {
          node {
            id
            calendar {
              id
            }
          }
        }
      }
    }
  `,
  basicSubjectDetails: gql`
    fragment subjectItem on Subject {
      id
      name
      isArchived
      metadata
      flatVariants(filters: $flatSubjectVariantFilters) {
        responseLanguage {
          id
          label
        }
      }
    }
  `,
};

export const externalCollaboratorFragment = {
  basicExternalCollaborator: gql`
    fragment externalUserItem on User {
      ...userNameFields
      email
      ... on Staff {
        role
        otherDetails {
          id
          organizationName
          phoneNumber
        }
      }
    }
    ${userNameFragment}
  `,
  basicExternalCollaboratorV2: gql`
    fragment externalUserItem on User {
      ...userNameFields
      email
      ... on Staff {
        yearGroups {
          id
          name
        }
        role
        otherDetails {
          id
          organizationName
          phoneNumber
        }
      }
    }
    ${userNameFragment}
  `,
};

export const projectTaskResponseFragment = gql`
  fragment projectTaskResponseItem on ProjectTaskResponse {
    id
    task {
      id
      title
      description
      dueOn
      closesOn
      submission {
        id
        type
      }
    }
    isComplete
    latestSubmissionResponse {
      id
      status
    }
  }
`;

//these are decoupled from unitPlan fragments as their requirements changed so their queries are now breaking projects
//for now we are updating only the affected areas
//TODO: these fragments needs to be cleaned up ,keep only which are required in projects

// Local copies of pypElementNodeFragment sub-fragments (from UnitPlanFragments)
const projectFocusAreaFragment = gql`
  fragment focusAreaItem on FocusArea {
    id
    label
  }
`;

const projectGenericTagFragment = gql`
  fragment basicGenericTagItem on GenericTag {
    id
    type
    label
  }
`;

const projectStandardSetFragment = gql`
  fragment standardSetItem on BenchmarkStandardSet {
    id
    title
    description
    code
  }
`;

// Local copies of pypElementFragment sub-fragments (from UnitPlanFragments)
const projectPypElementFragment = {
  theme: gql`
    fragment themeItem on PlannerTheme {
      id
      label
      descriptor
      metadata
      associatedChildren {
        id
        label
      }
      focusAreas {
        ...focusAreaItem
      }
      iconUrl: icon
    }
    ${projectFocusAreaFragment}
  `,
  lp: gql`
    fragment lpItem on PlannerLearnerProfile {
      id
      label
      subText
      levelId
      parent
      children
    }
  `,
  concept: gql`
    fragment conceptItem on PlannerConcept {
      id
      label
      subText
      levelId
      parent
      children
    }
  `,
  atl: gql`
    fragment atlItem on PlannerATL {
      id
      label
      children
      parent
      levelId
      levels {
        id
        value
      }
      depth
      isLeaf
      displaySequence
      atlCategory {
        id
      }
      subText
      dataSetNodeWrapper {
        properties {
          isArchived
          grades {
            id
            name
          }
        }
      }
    }
  `,
  benchmark: gql`
    fragment benchmarkItem on PlannerBenchmark {
      id
      label
      subText
      children
      parent
      subject
      displaySequence
      depth
      code
      dataSetNodeWrapper {
        standardSet {
          id
          title
        }
        properties {
          grades {
            id
            name
          }
          subject {
            id
            isArchived
          }
        }
      }
    }
  `,
  relatedConcept: gql`
    fragment relatedConceptItem on PlannerRelatedConcept {
      id
      label
      elementId
      elementType
      parent
      children
    }
  `,
  action: gql`
    fragment actionItem on Action {
      id
      label
      subText
      children
      parent
      levels {
        id
        value
      }
      levelId
    }
  `,
  attachmentGroup: gql`
    fragment attachmentGroupItem on AttachmentGroup {
      id
      metadata
      attachments {
        ... on Attachment {
          id
          name
          type
          mimeType
          url
          signedUrl
          thumbUrl
          title
        }
      }
      studentTemplate {
        id
      }
    }
  `,
  attachment: gql`
    fragment attachmentItem on Attachment {
      id
      name
      type
      mimeType
      url
      signedUrl
      thumbUrl
      title
      metadata
      streamUrl
      parentType
      isRead
      similarityReport {
        status
        submissionId
        errorCode
        overallMatchPercentage
      }
      createdBy {
        id
        type
      }
      lockingState {
        state
        scheduledAt
      }
      questionAttachment {
        ... on QuestionSubmission {
          id
          questionScore
          score {
            id
            value
          }
        }
        ... on Question {
          id
          questionScore
        }
      }
    }
  `,
  fieldScore: gql`
    fragment fieldScoreItem on ResolvedFieldScore {
      id
      type
      isUsingScore
      maxScore
      category {
        id
        label
        colorCode
        set {
          id
          associatedEntities {
            id
            ... on Course {
              id
              title
            }
            ... on Subject {
              id
              name
            }
          }
        }
      }
    }
  `,
  subjectGroupWithGlobalSubjectGroup: gql`
    fragment subjectGroupWithGlobalSubjectGroupItem on SubjectGroup {
      id
      name
      constants
      globalSubjectGroup {
        id
        name
        constants
      }
    }
  `,
};

// Local copy of subjectGroupFragment.subjectBenchmarkDetails (from UnitPlanFragments)
const projectSubjectGroupFragment = {
  subjectBenchmarkDetails: gql`
    fragment subjectItem on Subject {
      id
      name
      icon
      grades {
        id
        name
      }
      isArchived
      benchmarkRootNode {
        ...benchmarkItem
      }
      globalSubject
      framework
      snsChildren
      standardSet {
        ...standardSetItem
      }
      subjectLevelLabels {
        cuLevelName
        strandLevelName
        loLevelName
      }
      type
      tags {
        id
        key
        value
      }
      associatedLevels {
        id
        label
        isArchived
      }
      variants {
        level {
          ...basicGenericTagItem
        }
        responseLanguages {
          ...basicGenericTagItem
        }
      }
      flatVariants(filters: { archivalState: ALL }) {
        isArchived
        level {
          ...basicGenericTagItem
        }
        responseLanguage {
          ...basicGenericTagItem
        }
      }
    }
    ${projectPypElementFragment.benchmark}
    ${projectGenericTagFragment}
    ${projectStandardSetFragment}
  `,
};

// Local copy of responseFragment (from UnitPlanFragments)
const projectResponseFragment = {
  response: gql`
    fragment responseItem on Response {
      id
      message
      createdBy {
        id
        ... on Staff {
          ...staffItem
        }
        ... on Student {
          ...studentItem
        }
      }
      evidence {
        id
        ... on Attachment {
          ...attachmentItem
        }
        ... on Post {
          id
          title
          attachments {
            id
            name
            type
            signedUrl
          }
          items {
            item {
              id
              ... on StudentAssignment {
                id
                assignment {
                  id
                  content {
                    id
                    ... on Assessment {
                      id
                      assessmentType {
                        id
                        value
                      }
                    }
                  }
                }
              }
            }
            itemType
          }
        }
      }
      createdAt
      updatedAt
    }
    ${staffFragment.basicStaffDetails}
    ${studentFragment.basicStudentDetails}
    ${attachmentFragment.attachment}
  `,
};

// Local copy of the old plannerElementNode shape used in project fields.
// Decoupled from UnitPlanFragments so changes there do not affect Projects.
const projectPlannerElementNodeFragment = gql`
  fragment projectPlannerElementNodeItem on PlannerElementNode {
    id
    displaySequence
    label
    subText
    code
    type
    depth
    children
    parent
    subType
    isLeaf
    metadata
    updatedAt
    associatedParents {
      id
      ... on Grade {
        id
        name
      }
      ... on Subject {
        id
        name
        isArchived
      }
      ... on SubjectGroup {
        id
        name
        constants
      }
      ... on PlannerElementNode {
        id
        label
        type
        metadata
      }
    }
    dataSetNodeWrapper {
      properties {
        isGraded
      }
    }
  }
`;

// Project-specific resolved minimal tree — uses the local projectPlannerElementNodeItem shape
// (without nonPypPlannerElementNodeResolvedValue) so Projects stay decoupled from UnitPlans changes.
const projectResolvedFieldValueItemFragment = gql`
  fragment projectResolvedFieldValueItem on ResolvedFieldValue {
    id
    type
    ... on ResolvedFieldBenchmarkSet {
      id
      benchmarks {
        ...benchmarkItem
      }
    }
    ... on ResolvedFieldConceptSet {
      id
      concepts {
        ...conceptItem
      }
    }
    ... on ResolvedFieldActionSet {
      id
      actions {
        ...actionItem
      }
    }
    ... on ResolvedFieldLPSet {
      id
      learnerProfiles {
        ...lpItem
      }
    }
    ... on ResolvedFieldThemeSet {
      id
      themes {
        ...themeItem
      }
    }
    ... on ResolvedFieldSubjectSet {
      id
      subjects {
        ...subjectItem
        subjectGroup {
          id
          name
          constants
          globalSubjectGroup {
            id
            name
            constants
          }
        }
      }
    }
    ... on ResolvedFieldATLSet {
      id
      atls {
        ...atlItem
      }
    }
    ... on ResolvedFieldRelatedConceptSet {
      id
      relatedConcepts {
        ...relatedConceptItem
      }
    }
    ... on ResolvedFieldVideoAttachment {
      id
      attachment {
        ...attachmentItem
      }
    }
    ... on ResolvedFieldStudentTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldTags {
      id
      tags {
        id
        label
      }
    }
    ... on ResolvedFieldGenericTags {
      id
      genericTags {
        id
        label
        abbreviation
        __typename
      }
      __typename
    }
    ... on ResolvedFieldPlannerElementSet {
      id
      type
      nodes {
        ...projectPlannerElementNodeItem
      }
    }
    ... on ResolvedFieldSubjectGroup {
      id
      type
      subjectGroups {
        ...subjectGroupWithGlobalSubjectGroupItem
      }
    }
    ... on ResolvedFieldScore {
      ...fieldScoreItem
    }
    ... on ResolvedFieldObjectiveLevel {
      id
      nodes {
        id
        label
        dataSetNodeWrapper {
          properties {
            subjects {
              id
            }
          }
        }
      }
    }
    ... on ResolvedFieldAttachments {
      id
      type
      attachments {
        ...attachmentItem
      }
    }
  }
  ${projectPlannerElementNodeFragment}
  ${projectPypElementFragment.benchmark}
  ${projectPypElementFragment.concept}
  ${projectPypElementFragment.action}
  ${projectPypElementFragment.lp}
  ${projectPypElementFragment.theme}
  ${projectPypElementFragment.atl}
  ${projectPypElementFragment.relatedConcept}
  ${projectPypElementFragment.fieldScore}
  ${projectPypElementFragment.attachment}
  ${projectPypElementFragment.attachmentGroup}
  ${projectPypElementFragment.subjectGroupWithGlobalSubjectGroup}
  ${projectSubjectGroupFragment.subjectBenchmarkDetails}
  ${attachmentFragment.attachment}
`;

export const projectFieldItemFragment = {
  projectFieldItem: gql`
    fragment projectFieldItem on PlannerField {
      id
      uid
      value
      parent
      children
      isComplete
      updatedAt
      pseudoFieldUid
      attachments {
        ...attachmentItemV2
        presetAttachment {
          ...attachmentItem
        }
      }
      comments {
        totalCount
        unreadCount
        unreadCountV2
      }
      resolvedMinimalTree {
        ...projectResolvedFieldValueItem
      }
      responses: allResponses {
        totalCount
        edges {
          node {
            ...responseItem
          }
        }
      }
    }
    ${projectResponseFragment.response}
    ${projectResolvedFieldValueItemFragment}
    ${projectAttachmentFragment.attachment}
  `,
};

// Local copy of fieldsResolvedMinimalTreeFragment — decoupled from UnitPlanFragments.
export const projectFieldsResolvedMinimalTreeFragment = {
  fieldsResolvedMinimalTreeFragment: gql`
    fragment fieldsResolvedMinimalTreeFragmentItem on PlannerField {
      id
      uid
      value
      pseudoFieldUid
      children
      isComplete
      attachments {
        ...attachmentItem
      }
      comments {
        unreadCountV2
      }
      resolvedMinimalTree {
        id
        type
        ... on ResolvedFieldPlannerElementSet {
          id
          type
          ... on ResolvedFieldPlannerElementSet {
            id
            type
            nodes {
              id
              label
              type
              metadata
            }
          }
        }
      }
    }
    ${attachmentFragment.attachment}
  `,
};
